import { loadAllEntities } from "../../../cached-data/actions"
import { tProject } from "../../../cached-data/types"
import { iCurrentUser, tFeatures, Thunk } from "../../../common/types"
import { stringColDefCreator, buttonColDefCreator, slideToggleColDefCreator } from "./standard-col-def-creators"
import { ManagePicklistAccessButton } from "../picklists/ManagePicklistAccessButton"
import { LinkToPicklistButton } from "../picklists/LinkToPicklistButton"
import { createModifierAnnotationColDefs } from "../../../tk-modifiers/utils"
import { getFlagEnabled } from "../../../getFlagValue"
import MultiChoiceCellEditor from "../cell-editors/multi-choice-cell-editor"
import LockableOptionListRenderer from "../cell-renderers/lockable-option-list-renderer"
import { timekeepingModifierRoleListTooltip } from "../../../common/ag-grid-custom-tooltip-formatters"
import SliderCellEditor from "../cell-editors/slider-cell-editor"

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
export const getTimekeepingModifiersDashboardSettings = (
    featureFlags: tFeatures,
    currentProject: tProject,
    currentUser: iCurrentUser
) => {
    const { user_role } = currentUser
    const canManage = user_role === "ADMIN"
    const settings = {
        tableName: "Timekeeping Modifiers",
        navId: "timekeepingModifiers",
        resources: ["timekeepingModifiersForDashboard"],
        filters: [],
        additionalQueryParams: {
            include_annotations: true,
        },
        initAction: (): Thunk => dispatch => {
            dispatch(loadAllEntities("companyGroups"))
            dispatch(loadAllEntities("projects", { forceBigPageSize: true }))
        },
        colDefs: [
            stringColDefCreator({
                field: "/name",
                headerName: "Name",
                sort: "asc",
            }),
            stringColDefCreator({
                editable: canManage,
                field: "/description",
                headerName: "Description",
                // TODO: Un-hide column when the backend supports the description field:
                // https://rhumbix.atlassian.net/browse/WA-7640
                hide: true,
                width: 300,
            }),
            ...createModifierAnnotationColDefs(),
            {
                headerName: "Allow New Values",
                field: "/can_field_add_items",
                hide: !getFlagEnabled("WA-8168-field-added-picklist-items"),
                // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
                editable: (params: any) => {
                    // Only custom modifiers allow editing of the role list
                    return params.data?.standard_tk_modifier_type === null
                },
                // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
                valueGetter: (params: any) => {
                    if (params.data?.can_field_add_items) {
                        return params.data?.can_field_add_items.sort()
                    }
                    return []
                },
                // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
                cellRenderer: LockableOptionListRenderer((data: any) => {
                    // Standard modifiers should be locked
                    return data?.standard_tk_modifier_type !== null
                }),
                cellEditor: MultiChoiceCellEditor,
                cellEditorPopup: true,
                cellEditorParams: {
                    choices: ["ADMIN", "FOREMAN", "OFFICE_STAFF", "PAYROLL_ADMIN", "PM"].map(role => ({
                        label: role.replace(/_/g, " "),
                        value: role,
                    })),
                },
                customTooltip: timekeepingModifierRoleListTooltip,
            },
            {
                ...buttonColDefCreator({ width: 150 }),
                cellRenderer: LinkToPicklistButton,
                headerName: "Picklist",
                hide: !canManage,
            },
            {
                ...slideToggleColDefCreator({
                    headerName: "Include with Signatures",
                    field: "/include_with_signatures",
                    shouldPreventActionFocus: true,
                    hide: !getFlagEnabled("WA-8835-se-mods-in-tk-sig"),
                    editable: true,
                    cellEditor: SliderCellEditor,
                    width: 190,
                }),
                headerTooltip: "Enable this to include modifiers in signatures.",
            },
            {
                ...slideToggleColDefCreator({
                    headerName: "Export with PDFs",
                    field: "/export_with_pdfs",
                    shouldPreventActionFocus: true,
                    hide: !getFlagEnabled("WA-8856-se-mods-in-tk-pdf"),
                    editable: true,
                    cellEditor: SliderCellEditor,
                    width: 150,
                }),
                headerTooltip: "Enable this to export modifiers with Timecard PDFs",
            },
            {
                ...buttonColDefCreator({ width: 150 }),
                cellRenderer: ManagePicklistAccessButton("picklist"),
                headerName: "Actions",
                hide: !canManage,
            },
        ],
        gridSettings: {
            rowHeight: 40,
            defaultColDef: {
                editable: false,
            },
        },
        otherSettings: {
            enableSearchBar: true,
        },
    }

    return settings
}

export const getGroupsTkModifierTabSettings = (
    featureFlags: tFeatures,
    currentProject: tProject,
    currentUser: iCurrentUser
) => {
    const base = getTimekeepingModifiersDashboardSettings(featureFlags, currentProject, currentUser)
    // Filter out any unnecessary columns
    const groupsDashboardExcludedColumns = ["Actions"]
    base.colDefs = base.colDefs.filter(
        colDef => colDef.headerName && !groupsDashboardExcludedColumns.includes(colDef.headerName)
    )

    // Add a column showing whether the selected group has auto-add enabled,
    // and insert it to the left of the "Groups with Auto-Add" column
    base.colDefs.splice(
        base.colDefs.findIndex(colDef => colDef.headerName === "Groups with Auto-Add"),
        0,
        stringColDefCreator({
            field: "/root_group_has_auto_add",
            headerName: "Auto-Add Enabled",
            // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
            valueGetter: ({ data }) => (data.root_group_has_auto_add ? "Yes" : "No"),
            width: 200,
        })
    )

    // Make everything read-only
    base.colDefs.forEach(colDef => (colDef.editable = false))

    return base
}
/* eslint-enable @typescript-eslint/explicit-module-boundary-types */
